<script setup lang="ts">
import type { ShallowRef } from 'vue';
import type { SubmitEventPromise } from 'vuetify';
import type { ReadonlyDeep } from 'type-fest';
import type {
  InitialData as NewAirRoadChangeWeightDialogFormInitialData,
  ValidData as NewAirRoadChangeWeightDialogFormValidData,
} from '@/components/dialogForms/NewAirRoadChangeWeightDialogForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, reactive, ref, useTemplateRef, watch } from 'vue';
import _isEqual from 'lodash/isEqual';
import _upperFirst from 'lodash/upperFirst';

import { getStrCell } from '@/utils/file';
import KBtn from '@/components/btns/KBtn.vue';
import NewAirRoadChangeWeightDialogForm from '@/components/dialogForms/NewAirRoadChangeWeightDialogForm.vue';
import CloseWindowIconBtn from '@/components/iconBtns/CloseWindowIconBtn.vue';
import NewAirRoadPackagedProductsBasicListItem from '@/components/items/NewAirRoadPackagedProductsBasicListItem.vue';
import NewAirRoadPopoverVMenu from '@/components/vMenus/NewAirRoadPopoverVMenu.vue';
import * as airRoadHelpers from '@/helpers/airRoad';

export interface Props {
  pickupProducts?: ReadonlyDeep<
    openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products']
  >;
  packageNum?: number;
}
const props = withDefaults(defineProps<Props>(), {
  pickupProducts: () => [],
  packageNum: 0,
});
const emit = defineEmits<{
  packageUnpack: [packageNum: number, productNum?: number];
  'dragover': [
    event: DragEvent,
    rootEl: Readonly<ShallowRef<HTMLLIElement | null>>,
  ];
  'dragleave': [
    event: DragEvent,
    rootEl: Readonly<ShallowRef<HTMLLIElement | null>>,
  ];
  'drop': [
    event: DragEvent,
    rootEl: Readonly<ShallowRef<HTMLLIElement | null>>,
    packageNum: number,
  ];
}>();

const productsPackageModel = defineModel<
  openapi.AirroadSingleCreateBodyOpenapi['items'][0]
>({
  default: () => ({}),
});

const isChangeWeightMenu = ref(false);

const defaultWeight = computed(function () {
  return airRoadHelpers.getDefaultWeight(
    productsPackageModel.value.products,
    props.pickupProducts,
  );
});

const changeWeightInitialData =
  reactive<NewAirRoadChangeWeightDialogFormInitialData>({});

watch(
  function () {
    return productsPackageModel.value.weight;
  },
  function (newValues, oldValues) {
    if (_isEqual(newValues, oldValues)) return;
    changeWeightInitialData.weight = productsPackageModel.value.weight;
  },
  { immediate: true },
);

function handleChangeWeightClick(): void {
  isChangeWeightMenu.value = !isChangeWeightMenu.value;
}

function handleChangeWeightSubmit(
  event: SubmitEventPromise | null | undefined,
  changeWeightFormData: NewAirRoadChangeWeightDialogFormValidData,
): void {
  productsPackageModel.value.weight = changeWeightFormData.weight;
  // close on Enter (manual submit)
  if (event) {
    isChangeWeightMenu.value = false;
  }
}

const totalWeightEl = useTemplateRef('totalWeight');

function handleCloseClick(): void {
  emit('packageUnpack', props.packageNum);
}

function handleProductUnpack(productNum: number): void {
  emit('packageUnpack', props.packageNum, productNum);
}

const rootEl = useTemplateRef('root');

function handlePackageDragover(event: DragEvent): void {
  emit('dragover', event, rootEl);
}

function handlePackageDragleave(event: DragEvent): void {
  emit('dragleave', event, rootEl);
}

function handlePackageDrop(event: DragEvent): void {
  emit('drop', event, rootEl, props.packageNum);
}
</script>

<template>
  <li
    ref="root"
    :class="$style.newAirRoadPackagesBasicListItem"
    data-testid="newAirRoadPackagesBasicListItem"
    @dragover.prevent="handlePackageDragover"
    @dragleave.prevent="handlePackageDragleave"
    @drop.prevent="handlePackageDrop"
  >
    <div :class="$style.header">
      <div :class="$style.title">
        <div :class="$style.titleText">
          {{ _upperFirst(getStrCell(productsPackageModel.item_type)) }} #{{
            props.packageNum
          }}: {{ getStrCell(productsPackageModel.products?.length) }} items
        </div>
        <div :class="$style.close">
          <CloseWindowIconBtn
            :class="$style.closeBtn"
            @click="handleCloseClick"
          />
        </div>
      </div>
      <div :class="$style.total">
        <div :class="$style.totalText">Total</div>
        <div :class="$style.totalVolume">
          {{ getStrCell(productsPackageModel.volume) }}&nbsp;m³
        </div>
        <div ref="totalWeight" :class="$style.totalWeight">
          <span
            :class="[
              $style.totalWeightText,
              {
                [$style.isTotalWeightChanged]:
                  defaultWeight !== productsPackageModel.weight,
              },
            ]"
          >
            {{ getStrCell(productsPackageModel.weight) }}&nbsp;kg
          </span>
        </div>
        <div :class="$style.totalRight">
          <KBtn
            priority="info"
            variant="text"
            prepend-icon="mdi-pencil"
            size="xs"
            :active="isChangeWeightMenu"
            @click="handleChangeWeightClick"
          >
            Change weight
          </KBtn>
        </div>
      </div>
    </div>
    <div :class="$style.main">
      <ul :class="$style.packagedProductsBasicList">
        <NewAirRoadPackagedProductsBasicListItem
          v-for="(product, index) in productsPackageModel.products"
          :key="index"
          :product="product"
          :product-num="index + 1"
          :pickup-products="pickupProducts"
          :class="$style.packagedProductsBasicListItem"
          @product-unpack="handleProductUnpack"
        />
      </ul>
    </div>
    <NewAirRoadPopoverVMenu
      v-model="isChangeWeightMenu"
      :close-on-content-click="false"
      location="top"
      no-click-animation
      persistent
      scroll-strategy="none"
      :target="totalWeightEl"
    >
      <template #header>Set new value:</template>
      <NewAirRoadChangeWeightDialogForm
        :initial-data="changeWeightInitialData"
        @submit="handleChangeWeightSubmit"
      />
    </NewAirRoadPopoverVMenu>
  </li>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.newAirRoadPackagesBasicListItem {
  line-height: 1.4;
  background-color: colors.$color-white;
  border: 1px solid colors.$color-gray-200;
  border-radius: 4px;
  list-style-type: none;
}
  .header {
  }
    .title {
      display: flex;
      padding: 6px 16px;
      background-color: colors.$color-gray-100;
      min-height: 36px;
      align-items: start;
      justify-content: center;
    }
      .titleText {
        width: 100%;
        font-weight: 500;
      }
      .close {
        flex-shrink: 0;
        margin-right: -16px;
      }
        .closeBtn {
          margin: -8px 0 -6px 0;
        }
    .total {
      border-bottom: 1px solid colors.$color-gray-200;
      display: flex;
      align-items: center;
      padding-left: 16px;
    }
      .totalText {
        font-weight: 500;
        width: 100%;
      }
      .totalVolume {
        padding-left: 16px;
      }
      .totalWeight {
        padding: 0 12px;
      }
        .totalWeightText {
          padding: 4px;
          line-height: 1;
          display: inline-block;
        }
        .isTotalWeightChanged {
          background-color: colors.$color-brand-50;
        }
      .totalRight {
        margin-left: auto;
      }
  .main {
  }
    .packagedProductsBasicList {
      margin: 0;
      padding: 0;
    }
      .packagedProductsBasicListItem:first-child {
        border-top: 0;
      }
      .packagedProductsBasicListItem:last-child {
        border-bottom: 0;
      }
      .packagedProductsBasicListItem + .packagedProductsBasicListItem {
        border-top: 0;
      }
</style>
